// src/components/union-settings/banks/UnionBankWizardDialog.tsx
import {
  Badge,
  Box,
  Button,
  CloseButton,
  createListCollection,
  Dialog,
  Field,
  Flex,
  Grid,
  HStack,
  Input,
  Portal,
  Select,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import type { UnionBank } from '@/common/unionBank.common'
import UnionDiscardChangesDialog from './UnionDiscardChangesDialog'
import { toast } from '@/components/ui/toaster/toasterHelpers'
import { LuArrowRight, LuCheck, LuX } from 'react-icons/lu'
import {
  SALES_REGION_OPTIONS,
  SALES_REGION_VALUES,
  parseSalesRegions,
  joinSalesRegions,
  salesRegionLabel,
} from '@/common/salesRegion'

type Mode = 'create' | 'edit'

export type UnionBankWizardValues = {
  // step 1
  name: string
  name2: string
  name3: string
  sapDebtorId: string
  businessPartner: string

  // step 2
  salesRegion: string
  postal: string
  city: string
  street: string
  streetNumber: string
  country: string

  // step 3
  blz: string
  accountingArea: string
  centralBookingBlock: string
  vatNumber: string

  // contact — org-wide e-mail; the per-user Ansprechpartner (contactDetails)
  // is user-specific and hidden from the Union view (SIB-876)
  internalContactEmail: string
}

const VALIDATION_PATTERNS = {
  COMPANY: /^[\p{L}0-9 .,'&()\-]*$/u,
  STREET: /^[\p{L}0-9 .,'\-\/]*$/u,
  STREET_NUMBER: /^[0-9A-Za-z]+(?:[\/-][0-9A-Za-z]+)*$|^$/,
  POSTAL: /^[A-Za-z0-9 \-]{0,16}$/,
  CITY: /^[\p{L} .'-]*$/u,
  COUNTRY: /^(de|at|ch)$/,
  BLZ: /^[0-9]{0,8}$/,
}

const FullSchema = Yup.object({
  name: Yup.string()
    .trim()
    .required('Dieses Feld ist erforderlich.')
    .max(250, 'Maximal 250 Zeichen erlaubt.')
    .matches(VALIDATION_PATTERNS.COMPANY, 'Ungültige Zeichen im Banknamen.'),

  name2: Yup.string().trim().max(250, 'Maximal 250 Zeichen erlaubt.'),

  name3: Yup.string().trim().max(250, 'Maximal 250 Zeichen erlaubt.'),

  sapDebtorId: Yup.string().trim().max(50, 'Maximal 50 Zeichen erlaubt.'),

  businessPartner: Yup.string().trim().max(50, 'Maximal 50 Zeichen erlaubt.'),

  postal: Yup.string()
    .trim()
    .max(250, 'Maximal 250 Zeichen erlaubt.')
    .matches(VALIDATION_PATTERNS.POSTAL, 'Ungültige Zeichen in der Postleitzahl.'),

  city: Yup.string()
    .trim()
    .max(250, 'Maximal 250 Zeichen erlaubt.')
    .matches(VALIDATION_PATTERNS.CITY, 'Ungültige Zeichen im Ort.'),

  street: Yup.string()
    .trim()
    .max(250, 'Maximal 250 Zeichen erlaubt.')
    .matches(VALIDATION_PATTERNS.STREET, 'Ungültige Zeichen in der Straße.'),

  streetNumber: Yup.string()
    .trim()
    .max(250, 'Maximal 250 Zeichen erlaubt.')
    .matches(VALIDATION_PATTERNS.STREET_NUMBER, 'Ungültige Zeichen in der Hausnummer.'),

  country: Yup.string()
    .trim()
    .test(
      'country-valid',
      'Land muss de, at oder ch sein.',
      v => !v || VALIDATION_PATTERNS.COUNTRY.test(v)
    ),

  salesRegion: Yup.string()
    .trim()
    .test('sales-regions-valid', 'Ungültiges Vertriebsgebiet.', value =>
      parseSalesRegions(value).every(region => SALES_REGION_VALUES.includes(region))
    ),

  blz: Yup.string()
    .trim()
    .max(8, 'Maximal 8 Zeichen erlaubt.')
    .matches(VALIDATION_PATTERNS.BLZ, 'BLZ darf nur aus Ziffern bestehen (max. 8).'),

  accountingArea: Yup.string().trim().max(50, 'Maximal 50 Zeichen erlaubt.'),

  centralBookingBlock: Yup.string().trim().max(50, 'Maximal 50 Zeichen erlaubt.'),

  vatNumber: Yup.string().trim().max(50, 'Maximal 50 Zeichen erlaubt.'),

  internalContactEmail: Yup.string()
    .trim()
    .max(250, 'Maximal 250 Zeichen erlaubt.')
    .email('Bitte geben Sie eine gültige E-Mail-Adresse ein.'),
})
const STEP_FIELDS: (keyof UnionBankWizardValues)[][] = [
  ['name', 'name2', 'name3', 'sapDebtorId', 'businessPartner'],
  ['salesRegion', 'postal', 'city', 'street', 'streetNumber', 'country'],
  ['blz', 'accountingArea', 'centralBookingBlock', 'vatNumber', 'internalContactEmail'],
]

function getApiErrorInfo(error: any) {
  const status = error?.status ?? error?.response?.status ?? error?.cause?.status

  const data = error?.response?.data ?? error?.data ?? error?.cause?.data

  const message = data?.message ?? error?.message ?? 'Unbekannter Fehler'

  const code = data?.code ?? error?.code ?? error?.cause?.code

  return { status, message, code }
}
export default function UnionBankWizardDialog({
  mode,
  open,
  onOpenChange,
  initial,
  onSubmit,
}: {
  mode: Mode
  open: boolean
  onOpenChange: (open: boolean) => void
  initial: UnionBank | null
  onSubmit: (values: UnionBankWizardValues) => Promise<void>
}) {
  const [step, setStep] = useState(0)
  const [discardOpen, setDiscardOpen] = useState(false)
  const [saving, setSaving] = useState(false)

  const initialValues: UnionBankWizardValues = useMemo(
    () => ({
      name: initial?.name ?? '',
      name2: initial?.name2 ?? '',
      name3: initial?.name3 ?? '',
      sapDebtorId: initial?.sapDebtorId ?? '',
      businessPartner: initial?.businessPartner ?? '',
      accountingArea: initial?.accountingArea ?? '',
      centralBookingBlock: initial?.centralBookingBlock ?? '',
      vatNumber: initial?.vatNumber ?? '',

      salesRegion: initial?.salesRegion ?? '',
      postal: initial?.postal ?? '',
      city: initial?.city ?? '',
      street: initial?.street ?? '',
      streetNumber: initial?.streetNumber ?? '',
      country: initial?.country ?? '',
      blz: initial?.blzNum ?? '',
      internalContactEmail: initial?.internalContactEmail ?? '',
    }),
    [initial]
  )

  const formik = useFormik<UnionBankWizardValues>({
    initialValues,
    enableReinitialize: true,
    validateOnMount: false,
    validateOnBlur: true,
    validateOnChange: true,
    validationSchema: FullSchema,
    onSubmit: async values => {
      setSaving(true)
      try {
        const normalizedValues: UnionBankWizardValues = {
          ...values,
          name: values.name.trim(),
          name2: values.name2.trim(),
          name3: values.name3.trim(),
          sapDebtorId: values.sapDebtorId.trim(),
          businessPartner: values.businessPartner.trim(),
          salesRegion: values.salesRegion.trim(),
          postal: values.postal.trim(),
          city: values.city.trim(),
          street: values.street.trim(),
          streetNumber: values.streetNumber.trim(),
          country: values.country.trim(),
          blz: values.blz.trim(),
          accountingArea: values.accountingArea.trim(),
          centralBookingBlock: values.centralBookingBlock.trim(),
          vatNumber: values.vatNumber.trim(),
          internalContactEmail: values.internalContactEmail.trim(),
        }

        await onSubmit(normalizedValues)
        toast.success(mode === 'create' ? 'Bank wurde angelegt' : 'Bank wurde aktualisiert')
        onOpenChange(false)
        setStep(0)
        formik.resetForm()
      } catch (error: any) {
        const { status, message, code } = getApiErrorInfo(error)

        if (
          status === 409 &&
          code === 'CONFLICT' &&
          message === 'A bank with this debitor ID already exists'
        ) {
          setStep(0)
          formik.setFieldTouched('sapDebtorId', true, false)
          formik.setFieldError('sapDebtorId', 'Eine Bank mit dieser Debitor-ID existiert bereits.')
          toast.error(
            'Debitor-ID ist bereits vergeben',
            'Eine Bank mit dieser Debitor-ID existiert bereits.'
          )
          return
        }

        toast.error(
          'Bank konnte nicht gespeichert werden',
          'Bitte prüfen Sie Ihre Eingaben und führen Sie den Vorgang erneut aus.'
        )
      } finally {
        setSaving(false)
      }
    },
  })

  useEffect(() => {
    if (!open) return

    setStep(0)
    setDiscardOpen(false)
    formik.resetForm({ values: initialValues })
  }, [open, initialValues])

  const title = mode === 'create' ? 'Bank anlegen' : 'Bank bearbeiten'
  const countryItems = createListCollection({
    items: [
      { value: 'de', label: 'DE' },
      { value: 'at', label: 'AT' },
      { value: 'ch', label: 'CH' },
    ],
  })

  const salesRegionItems = createListCollection({
    items: SALES_REGION_OPTIONS,
  })

  const closeRequested = () => {
    if (formik.dirty) setDiscardOpen(true)
    else {
      onOpenChange(false)
      setStep(0)
    }
  }

  const next = async () => {
    const errors = await formik.validateForm()
    const currentFields = STEP_FIELDS[step]

    currentFields.forEach(field => {
      formik.setFieldTouched(field, true, false)
    })

    const hasStepErrors = currentFields.some(field => !!errors[field])
    if (hasStepErrors) return

    setStep(s => Math.min(2, s + 1))
  }
  const submitFinal = async () => {
    const errors = await formik.validateForm()

    Object.keys(formik.values).forEach(key => {
      formik.setFieldTouched(key as keyof UnionBankWizardValues, true, false)
    })

    if (Object.keys(errors).length > 0) return

    formik.handleSubmit()
  }

  const back = () => setStep(s => Math.max(0, s - 1))

  return (
    <>
      <Dialog.Root
        open={open}
        onOpenChange={e => {
          // If Chakra tries to close the dialog → intercept
          if (!e.open) {
            closeRequested()
          }
        }}
        size="xl"
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content
              data-testid="union-bank-wizard-dialog"
              rounded="2xl"
              shadow="0px 8px 16px 0px gray.muted;"
              maxW="3xl"
            >
              <Dialog.CloseTrigger asChild>
                <CloseButton
                  size="sm"
                  onClick={closeRequested}
                  mt="2"
                  mr="2"
                  borderWidth="1px"
                  borderColor="border.muted"
                />
              </Dialog.CloseTrigger>
              <Dialog.Header>
                <Dialog.Title fontSize="lg" fontWeight="600">
                  {title}
                </Dialog.Title>
                <Dialog.CloseTrigger onClick={closeRequested} />
              </Dialog.Header>

              <Dialog.Body>
                {/* Step header */}
                <WizardStepper
                  step={step}
                  steps={[{ label: 'Stammdaten' }, { label: 'Adresse' }, { label: 'Bankdaten' }]}
                />

                <Box borderWidth="1px" borderColor="border.muted" borderRadius="xl" p={4}>
                  {step === 0 && (
                    <VStack align="stretch" gap={4}>
                      <Row
                        label="Name"
                        name="name"
                        value={formik.values.name}
                        error={formik.touched.name ? (formik.errors.name as any) : undefined}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        placeholder="Musterbank"
                      />
                      <HStack gap={4} align="flex-start">
                        <Field.Root flex="1">
                          <Field.Label fontSize="sm" fontWeight="semibold">
                            Name 2{' '}
                            <Badge size="xs" variant="subtle" fontWeight="normal">
                              optional
                            </Badge>
                          </Field.Label>
                          <Input
                            name="name2"
                            value={formik.values.name2}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            placeholder="Musterbank"
                          />
                          <Field.ErrorText>
                            {formik.touched.name2 ? (formik.errors.name2 as string) : ''}
                          </Field.ErrorText>
                        </Field.Root>
                        <Field.Root flex="1">
                          <Field.Label fontSize="sm" fontWeight="semibold">
                            Name 3{' '}
                            <Badge size="xs" variant="subtle" fontWeight="normal">
                              optional
                            </Badge>
                          </Field.Label>
                          <Input
                            name="name3"
                            value={formik.values.name3}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            placeholder="Musterbank"
                          />
                          <Field.ErrorText>
                            {formik.touched.name3 ? (formik.errors.name3 as string) : ''}
                          </Field.ErrorText>
                        </Field.Root>
                      </HStack>
                      <HStack gap={4} align="flex-start">
                        <Row
                          label="Debitor"
                          name="sapDebtorId"
                          value={formik.values.sapDebtorId}
                          error={
                            formik.touched.sapDebtorId
                              ? (formik.errors.sapDebtorId as any)
                              : undefined
                          }
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            if (
                              formik.errors.sapDebtorId ===
                              'Eine Bank mit dieser Debitor-ID existiert bereits.'
                            ) {
                              formik.setFieldError('sapDebtorId', undefined as any)
                            }
                            formik.handleChange(e)
                          }}
                          onBlur={formik.handleBlur}
                          placeholder="z.B. 123456"
                          required={false}
                        />
                        <Row
                          label="Geschäftspartner"
                          name="businessPartner"
                          value={formik.values.businessPartner}
                          error={
                            formik.touched.businessPartner
                              ? (formik.errors.businessPartner as any)
                              : undefined
                          }
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          placeholder="z.B. GP-001"
                          required={false}
                        />
                      </HStack>
                    </VStack>
                  )}

                  {step === 1 && (
                    <VStack align="stretch" gap={4}>
                      {/* <HStack gap={4} align="flex-start">
                        <Row
                          label="PLZ"
                          name="postal"
                          value={formik.values.postal}
                          error={formik.touched.postal ? (formik.errors.postal as any) : undefined}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          placeholder="12345"
                          flex={1}
                          required={false}
                        />
                        <Row
                          label="Ort"
                          name="city"
                          value={formik.values.city}
                          error={formik.touched.city ? (formik.errors.city as any) : undefined}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          placeholder="Musterstadt"
                          flex={1}
                          required={false}
                        />
                      </HStack> */}

                      <HStack gap={4} align="flex-start">
                        <Field.Root
                          invalid={!!(formik.touched.salesRegion && formik.errors.salesRegion)}
                          flex={1}
                        >
                          <Field.Label fontSize="sm" fontWeight="semibold">
                            Vertriebsgebiet
                            <Badge size="xs" variant="subtle" fontWeight="normal" ml={2}>
                              optional
                            </Badge>
                          </Field.Label>

                          <Select.Root
                            multiple
                            collection={salesRegionItems}
                            value={parseSalesRegions(formik.values.salesRegion)}
                            onBlur={() => formik.setFieldTouched('salesRegion', true, true)}
                            onValueChange={({ value }) => {
                              formik.setFieldValue('salesRegion', joinSalesRegions(value))
                            }}
                          >
                            <Select.HiddenSelect />
                            <Select.Control>
                              <Select.Trigger>
                                <Select.ValueText placeholder="Bitte auswählen" />
                              </Select.Trigger>
                              <Select.IndicatorGroup>
                                <Select.Indicator />
                              </Select.IndicatorGroup>
                            </Select.Control>

                            <Select.Positioner>
                              <Select.Content>
                                {salesRegionItems.items.map(region => (
                                  <Select.Item item={region} key={region.value}>
                                    {region.label}
                                    <Select.ItemIndicator />
                                  </Select.Item>
                                ))}
                              </Select.Content>
                            </Select.Positioner>
                          </Select.Root>

                          {parseSalesRegions(formik.values.salesRegion).length > 0 && (
                            <Flex wrap="wrap" gap={2} mt={2}>
                              {parseSalesRegions(formik.values.salesRegion).map(region => (
                                <HStack
                                  key={region}
                                  gap={2}
                                  px={3}
                                  py={1}
                                  borderWidth="1px"
                                  borderColor="border.subtle"
                                  borderRadius="md"
                                  bg="bg.subtle"
                                >
                                  <Text fontSize="sm">{salesRegionLabel(region)}</Text>
                                  <Box
                                    as="button"
                                    type="button"
                                    aria-label={`${salesRegionLabel(region)} entfernen`}
                                    onClick={() =>
                                      formik.setFieldValue(
                                        'salesRegion',
                                        joinSalesRegions(
                                          parseSalesRegions(formik.values.salesRegion).filter(
                                            r => r !== region
                                          )
                                        )
                                      )
                                    }
                                    display="inline-flex"
                                    color="fg.muted"
                                    _hover={{ color: 'fg' }}
                                    cursor="pointer"
                                  >
                                    <LuX />
                                  </Box>
                                </HStack>
                              ))}
                            </Flex>
                          )}

                          <Field.ErrorText>
                            {formik.touched.salesRegion
                              ? (formik.errors.salesRegion as string)
                              : ''}
                          </Field.ErrorText>
                        </Field.Root>

                        <Row
                          label="PLZ"
                          name="postal"
                          value={formik.values.postal}
                          error={formik.touched.postal ? (formik.errors.postal as any) : undefined}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          placeholder="12345"
                          flex={1}
                          required={false}
                        />

                        <Row
                          label="Ort"
                          name="city"
                          value={formik.values.city}
                          error={formik.touched.city ? (formik.errors.city as any) : undefined}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          placeholder="Musterstadt"
                          flex={2}
                          required={false}
                        />
                      </HStack>

                      <HStack gap={4} align="flex-start">
                        <Row
                          label="Straße"
                          name="street"
                          value={formik.values.street}
                          error={formik.touched.street ? (formik.errors.street as any) : undefined}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          placeholder="Weißfrauenstr"
                          flex={2}
                          required={false}
                        />
                        <Row
                          label="Hausnummer"
                          name="streetNumber"
                          value={formik.values.streetNumber}
                          error={
                            formik.touched.streetNumber
                              ? (formik.errors.streetNumber as any)
                              : undefined
                          }
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          placeholder="01"
                          flex={1}
                          required={false}
                        />
                        <Field.Root
                          invalid={!!(formik.touched.country && formik.errors.country)}
                          flex={1}
                        >
                          <Field.Label fontSize="sm" fontWeight="semibold">
                            Länder/RegSchlüssel
                            <Badge size="xs" variant="subtle" fontWeight="normal" ml={2}>
                              optional
                            </Badge>
                          </Field.Label>

                          <Select.Root
                            collection={countryItems}
                            value={formik.values.country ? [formik.values.country] : []}
                            onBlur={() => formik.setFieldTouched('country', true, true)}
                            onValueChange={({ value }) => {
                              formik.setFieldValue('country', value[0] ?? '')
                              formik.setStatus(null)
                            }}
                          >
                            <Select.HiddenSelect />
                            <Select.Control>
                              <Select.Trigger>
                                <Select.ValueText placeholder="Bitte auswählen" />
                              </Select.Trigger>
                              <Select.IndicatorGroup>
                                <Select.Indicator />
                              </Select.IndicatorGroup>
                            </Select.Control>

                            <Select.Positioner>
                              <Select.Content>
                                {countryItems.items.map(country => (
                                  <Select.Item item={country} key={country.value}>
                                    {country.label}
                                    <Select.ItemIndicator />
                                  </Select.Item>
                                ))}
                              </Select.Content>
                            </Select.Positioner>
                          </Select.Root>

                          <Field.ErrorText>
                            {formik.touched.country ? (formik.errors.country as any) : ''}
                          </Field.ErrorText>
                        </Field.Root>
                      </HStack>
                    </VStack>
                  )}

                  {step === 2 && (
                    <VStack align="stretch" gap={4}>
                      <HStack gap={4} align="flex-start">
                        <Row
                          label="BLZ"
                          name="blz"
                          value={formik.values.blz}
                          error={formik.touched.blz ? (formik.errors.blz as any) : undefined}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const value = e.target.value.replace(/\D/g, '').slice(0, 8)
                            formik.setFieldValue('blz', value)
                          }}
                          onBlur={formik.handleBlur}
                          placeholder="z.B. 50010517"
                          required={false}
                        />
                        <Row
                          label="Buchungskreis"
                          name="accountingArea"
                          value={formik.values.accountingArea}
                          error={
                            formik.touched.accountingArea
                              ? (formik.errors.accountingArea as any)
                              : undefined
                          }
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          placeholder="z.B. 1000"
                          required={false}
                        />
                        <Row
                          label="Zentr. Buchungssperre"
                          name="centralBookingBlock"
                          value={formik.values.centralBookingBlock}
                          error={
                            formik.touched.centralBookingBlock
                              ? (formik.errors.centralBookingBlock as any)
                              : undefined
                          }
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          placeholder="z.B. X"
                          required={false}
                        />
                      </HStack>
                      <Box h="1px" bg="border.muted" />

                      <Text fontSize="sm" fontWeight="semibold">
                        Kontakt
                      </Text>
                      <Field.Root
                        invalid={
                          !!(
                            formik.touched.internalContactEmail &&
                            formik.errors.internalContactEmail
                          )
                        }
                      >
                        <Field.Label fontSize="sm" fontWeight="semibold">
                          Interne Kontakt-E-Mail
                          <Badge size="xs" variant="subtle" fontWeight="normal" ml={2}>
                            optional
                          </Badge>
                        </Field.Label>
                        <Input
                          name="internalContactEmail"
                          type="email"
                          value={formik.values.internalContactEmail}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          placeholder="mail@..."
                        />
                        <Field.ErrorText>
                          {formik.touched.internalContactEmail
                            ? (formik.errors.internalContactEmail as string)
                            : ''}
                        </Field.ErrorText>
                      </Field.Root>
                    </VStack>
                  )}
                </Box>
              </Dialog.Body>

              <Dialog.Footer>
                <Flex w="full" justify="flex-end" gap={3}>
                  <HStack gap={2}>
                    {step > 0 && (
                      <Button variant="outline" onClick={back} mb="2">
                        Zurück
                      </Button>
                    )}

                    {step < 2 ? (
                      <Button
                        variant="subtle"
                        size="md"
                        colorPalette="blue"
                        onClick={next}
                        borderWidth="1px"
                        borderColor="blue.muted"
                        mb="2"
                      >
                        <LuArrowRight />
                        Nächster Schritt
                      </Button>
                    ) : (
                      <Button
                        variant="solid"
                        bg="blue.solid"
                        loading={saving}
                        // disabled={!formik.isValid}
                        onClick={submitFinal}
                        mb="2"
                      >
                        {mode === 'create' ? 'Bank erstellen' : 'Speichern'}
                      </Button>
                    )}
                  </HStack>
                </Flex>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>

      <UnionDiscardChangesDialog
        open={discardOpen}
        onOpenChange={setDiscardOpen}
        onDiscard={() => {
          setDiscardOpen(false)
          onOpenChange(false)
          setStep(0)
          formik.resetForm()
        }}
      />
    </>
  )
}

function WizardStepper({ step, steps }: { step: number; steps: { label: string }[] }) {
  // supports 3 steps (your wizard). If you want dynamic N later, tell me.
  return (
    <Grid
      w="full"
      alignItems="center"
      // node, line, node, line, node
      templateColumns="auto 1fr auto 1fr auto"
      columnGap={6}
      mb={4}
    >
      {steps.map((s, idx) => {
        const done = idx < step
        const active = idx === step

        const Node = (
          <Flex key={`node-${idx}`} align="center" minW={0}>
            <Flex
              align="center"
              justify="center"
              w="34px"
              h="34px"
              borderRadius="full"
              flexShrink={0}
              bg={done ? 'blue.solid' : 'transparent'}
              borderWidth="2px"
              borderColor={done || active ? 'blue.solid' : 'border.muted'}
              color={done ? 'white' : active ? 'blue.fg' : 'fg.muted'}
            >
              {done ? <LuCheck /> : <Text fontWeight="semibold">{idx + 1}</Text>}
            </Flex>

            <Text
              ml={3}
              fontSize="sm"
              fontWeight={active ? 'semibold' : 'normal'}
              color={done || active ? 'fg.default' : 'fg.muted'}
              whiteSpace="nowrap"
            >
              {s.label}
            </Text>
          </Flex>
        )

        // grid placement: nodes go in col 1,3,5. lines go in col 2,4.
        const nodeCol = idx * 2 + 1
        const lineCol = idx * 2 + 2

        return (
          <>
            <Box key={`nodebox-${idx}`} gridColumn={nodeCol}>
              {Node}
            </Box>

            {idx < steps.length - 1 && (
              <Box
                key={`line-${idx}`}
                gridColumn={lineCol}
                h="2px"
                borderRadius="full"
                bg={idx < step ? 'blue.solid' : 'border.muted'}
                w="full"
              />
            )}
          </>
        )
      })}
    </Grid>
  )
}

function Row({
  label,
  name,
  value,
  error,
  onChange,
  onBlur,
  placeholder,
  flex,
  required = true,
}: {
  label: string
  name: string
  value: string
  error?: string
  onChange: any
  onBlur: any
  placeholder?: string
  flex?: number
  required?: boolean
}) {
  return (
    <Field.Root invalid={!!error} flex={flex}>
      <Field.Label fontSize="sm" fontWeight="semibold">
        {label}
        {required ? (
          <Text as="span" color="red.fg" fontWeight="normal">
            *
          </Text>
        ) : (
          <Badge size="xs" variant="subtle" fontWeight="normal" ml={2}>
            optional
          </Badge>
        )}
      </Field.Label>
      <Input
        name={name}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
      />
      <Field.ErrorText>{error ?? ''}</Field.ErrorText>
    </Field.Root>
  )
}
